import React, { useEffect, useState, useCallback } from 'react';
import {
  DollarSign, Eye, MousePointerClick, Percent, Users, MessageCircle,
  ArrowUp, ArrowDown, Activity, FileText, MessageSquare, AlertTriangle,
} from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { META_MARKET_FLAGS } from './MetaStatsPage';
import { MetaChannelBadge } from '../components/MetaChannelBadge';
import { fmtNum, fmtSpend, fmtPct, fmtDate } from '../lib/metaStatsFormat';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DAY_OPTIONS = [
  { value: 7,  label: '7 dagar' },
  { value: 14, label: '14 dagar' },
  { value: 30, label: '30 dagar' },
];

function DeltaBadge({ value }) {
  if (value === null || value === undefined) return null;
  const isUp = value >= 0;
  return (
    <span className={`mstat-delta${isUp ? ' mstat-delta--up' : ' mstat-delta--down'}`} data-testid="metastats-kpi-delta">
      {isUp ? <ArrowUp size={11} /> : <ArrowDown size={11} />}
      {Math.abs(value).toLocaleString('sv-SE', { maximumFractionDigits: 1 })}%
    </span>
  );
}

function StatCard({ icon: Icon, label, value, delta, color, unit }) {
  return (
    <div className="admin-stat-card" data-testid={`metastats-kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="admin-stat-icon" style={{ background: `${color}18`, color }}>
        <Icon size={18} />
      </div>
      <div className="mstat-kpi-body">
        <p className="admin-stat-label">{label}</p>
        <p className="admin-stat-value">
          {value}{unit && <span className="mstat-kpi-unit">{unit}</span>}
        </p>
        <DeltaBadge value={delta} />
      </div>
    </div>
  );
}

export default function MetaStatsOverviewTab({ accessToken, marketsParam }) {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API_URL}/api/meta-stats/overview?markets=${marketsParam}&days=${days}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setError('Kunde inte hämta Meta-statistik.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [accessToken, marketsParam, days]);

  useEffect(() => { load(); }, [load]);

  const deltas = data?.kpi_deltas || {};

  return (
    <div data-testid="metastats-overview-tab">
      <div className="mstat-tab-badge-row"><MetaChannelBadge type="paid" /></div>
      <div className="tab-switcher" style={{ marginBottom: 20 }}>
        {DAY_OPTIONS.map(o => (
          <button
            key={o.value}
            data-testid={`metastats-days-${o.value}`}
            className={`tab-btn${days === o.value ? ' tab-btn--active' : ''}`}
            style={{ fontSize: 12 }}
            onClick={() => setDays(o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>

      {error && <div className="admin-notice" data-testid="metastats-overview-error">{error}</div>}

      {loading ? (
        <div className="admin-loading">Laddar Meta-statistik...</div>
      ) : !data ? null : (
        <>
          <div className="admin-stats-row mstat-kpi-row">
            <StatCard icon={DollarSign}        label="Spend"       value={fmtSpend(data.kpi.spend)}      delta={deltas.spend}       color="#6366f1" unit="kr" />
            <StatCard icon={Eye}               label="Impressions" value={fmtNum(data.kpi.impressions)}  delta={deltas.impressions} color="#0ea5e9" />
            <StatCard icon={MousePointerClick} label="Clicks"      value={fmtNum(data.kpi.clicks)}       delta={deltas.clicks}      color="#22c55e" />
            <StatCard icon={Percent}           label="CTR"         value={fmtPct(data.kpi.ctr)}          delta={deltas.ctr}         color="#f59e0b" />
            <StatCard icon={Users}             label="Reach"       value={fmtNum(data.kpi.reach)}        delta={deltas.reach}       color="#ec4899" />
            <StatCard icon={MessageCircle}     label="CTA-klick"   value={fmtNum(data.kpi.cta_klick)}    color="#14b8a6" />
          </div>
          <p className="mstat-note" style={{ marginTop: -8 }}>
            Procentuell förändring jämfört med ögonblicksbilden för {days} {days === 1 ? 'dag' : 'dagar'} sedan.
          </p>

          <div className="mstat-activity-card" data-testid="metastats-recent-activity">
            <div className="mstat-activity-header">
              <Activity size={15} />
              <span>Senaste aktivitet (3 dagar)</span>
            </div>
            <div className="mstat-activity-stats">
              <span data-testid="metastats-activity-posts"><FileText size={13} /> {data.recent_activity.new_posts} nya inlägg</span>
              <span data-testid="metastats-activity-comments"><MessageSquare size={13} /> {data.recent_activity.new_comments} nya kommentarer</span>
              <span
                className={data.recent_activity.high_cost_campaigns > 0 ? 'mstat-activity-warn' : ''}
                data-testid="metastats-activity-highcost"
              >
                <AlertTriangle size={13} /> {data.recent_activity.high_cost_campaigns} kampanjer med kostnad/köp {'>'} 2x marknadsmedian
              </span>
            </div>
          </div>

          {data.errors && data.errors.length > 0 && (
            <div className="admin-notice" data-testid="metastats-overview-fel">
              ⚠ {data.errors.length} datainsamlingsfel under perioden — senaste: "{data.errors[data.errors.length - 1].fel_not}"
              ({data.errors[data.errors.length - 1].market})
            </div>
          )}

          <div className="admin-section">
            <h2 className="admin-section-title">Trend — Spend &amp; Impressions (all tillgänglig historik)</h2>
            <div className="mstat-chart-card" data-testid="metastats-trend-chart">
              {data.trend.length < 2 ? (
                <div className="admin-empty">För få dagar med data ännu för att visa en trendlinje — kommer fyllas på allt eftersom fler dagliga snapshots samlas in.</div>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={data.trend} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="mstatSpendGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.35} />
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="mstatImprGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.35} />
                        <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tickFormatter={fmtDate} fontSize={11} />
                    <YAxis yAxisId="spend" fontSize={11} />
                    <YAxis yAxisId="impr" orientation="right" fontSize={11} />
                    <Tooltip labelFormatter={fmtDate} contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 12 }} />
                    <Legend />
                    <Area yAxisId="spend" type="monotone" dataKey="spend" name="Spend" stroke="#6366f1" strokeWidth={2.2} fill="url(#mstatSpendGrad)" dot={{ r: 2.5 }} />
                    <Area yAxisId="impr" type="monotone" dataKey="impressions" name="Impressions" stroke="#0ea5e9" strokeWidth={2.2} fill="url(#mstatImprGrad)" dot={{ r: 2.5 }} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="admin-section">
            <h2 className="admin-section-title">Per marknad — {DAY_OPTIONS.find(o => o.value === days)?.label.toLowerCase()}</h2>
            <div className="admin-table" data-testid="metastats-per-market-table">
              <div className="admin-table-header" style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr 0.8fr 1fr' }}>
                <span>Marknad</span><span>Spend</span><span>Impressions</span><span>Clicks</span><span>CTR</span><span>Reach</span>
              </div>
              {data.per_market.length === 0 ? (
                <div className="admin-empty">Ingen data för valda marknader/period.</div>
              ) : data.per_market.map(r => (
                <div key={r.market} className="admin-table-row" style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr 0.8fr 1fr' }} data-testid={`metastats-permarket-row-${r.market}`}>
                  <span>{META_MARKET_FLAGS[r.market] || ''} {r.market}</span>
                  <span>{fmtSpend(r.spend)}</span>
                  <span>{fmtNum(r.impressions)}</span>
                  <span>{fmtNum(r.clicks)}</span>
                  <span>{fmtPct(r.ctr)}</span>
                  <span>{fmtNum(r.reach)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="admin-section">
            <h2 className="admin-section-title">Sidprestanda (Facebook)</h2>
            <div className="admin-table" data-testid="metastats-page-performance">
              <div className="admin-table-header" style={{ gridTemplateColumns: '1.2fr 1fr 1fr 1fr' }}>
                <span>Marknad</span><span>Varumärke</span><span>Engagemang</span><span>Följare</span>
              </div>
              {data.page_performance.length === 0 ? (
                <div className="admin-empty">Ingen sidata tillgänglig.</div>
              ) : data.page_performance.map(r => (
                <div key={r.market} className="admin-table-row" style={{ gridTemplateColumns: '1.2fr 1fr 1fr 1fr' }} data-testid={`metastats-page-row-${r.market}`}>
                  <span>{META_MARKET_FLAGS[r.market] || ''} {r.market}</span>
                  <span>{r.brand}</span>
                  <span>{fmtNum(r.engagemang)}</span>
                  <span>{fmtNum(r.foljare)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="admin-section">
            <h2 className="admin-section-title">Instagram-prestanda</h2>
            <p className="mstat-note">
              Observera: <strong>Color4care (delat konto: DE/NO/FI/DK)</strong> är ETT Instagram-konto som täcker fyra marknader gemensamt — siffrorna nedan är inte uppdelade per land.
            </p>
            <div className="admin-table" data-testid="metastats-ig-performance">
              <div className="admin-table-header" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr' }}>
                <span>Konto / Etikett</span><span>Varumärke</span><span>Räckvidd</span><span>Följare</span>
              </div>
              {data.instagram_performance.length === 0 ? (
                <div className="admin-empty">Ingen Instagram-data tillgänglig.</div>
              ) : data.instagram_performance.map(r => (
                <div key={r.marknad_etikett} className="admin-table-row" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr' }} data-testid={`metastats-ig-row-${r.marknad_etikett}`}>
                  <span>{r.marknad_etikett}</span>
                  <span>{r.brand}</span>
                  <span>{fmtNum(r.rackvidd)}</span>
                  <span>{fmtNum(r.foljare)}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
